import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { Box, Typography, Skeleton } from "@mui/material"
import { useApiQuery } from "../../../../../../../core/api/useApiQuery"
import { fetchKitchenTakeawayOffers, KitchenTakeawayOffersResponse, UserWeekMenu } from "../../../weekMenuApi"
import { Products } from "../../../../../../merchant/admin/priceList/priceListTypes"
import { ProductOffer, ProductOfferEmpty } from "./ProductOffer"
import { makeStyles } from "tss-react/mui"
import { QueryErrorBoundary } from "../../../../../../../core/api/components/QueryErrorBoundary"
import { ErrorBoundary } from "react-error-boundary"
import { UserDashboardErrorFallbackCard } from "../../UserDashboardErrorFallbackCard"
import { useAppSelector } from "../../../../../../store/storeHooks"
import { selectKitchen } from "../../../../../nonAdminUserSelectors"
import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { RefetchIntervals } from "../../../../../../../core/api/refetchIntervals"
import { selectEffectiveUserId } from "../../../../../../context/contextSelectors"

const transformOffersData = (data: KitchenTakeawayOffersResponse): KitchenTakeawayOffersResponse["menues"] => {
    return data.menues
}

const useStyles = makeStyles()((theme) => ({
    container: {
        padding: "1.25rem",
        borderRadius: ".75rem",
        backgroundColor: "white",
        marginBottom: "3rem",
        [theme.breakpoints.up("xl")]: {
            padding: "2.25rem",
        },
    },
    newsGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        // gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
        gap: "1.25rem",
    },
    skeletonCard: {
        height: "9rem",
        borderRadius: ".75rem",
        width: "100%",
    },
    errorCard: {
        backgroundColor: "transparent",
        boxShadow: "none",
    },
    errorContent: {
        paddingTop: 0,
        paddingLeft: 0,
        paddingRight: 0,
    },
}))

export function SafeOffersSection() {
    const { classes } = useStyles()
    const selectedKitchen = useAppSelector(selectKitchen)
    const userId = useAppSelector(selectEffectiveUserId)

    const { data: offersMenu, isLoading } = useApiQuery(fetchKitchenTakeawayOffers, {
        queryName: UserWeekMenu.KITCHEN_TAKEAWAY_OFFERS,
        dependencies: {
            kitchenId: selectedKitchen?.id ?? 0,
        },
        enabled: Boolean(selectedKitchen?.id),
        select: transformOffersData,
        useErrorBoundary: true,
        refetchInterval: RefetchIntervals.medium,
    })

    const isOffersMenu = Array.isArray(offersMenu) && offersMenu.length > 0
    const hasProducts = isOffersMenu && offersMenu.some((menu) => menu.products && menu.products.length > 0)

    if (!selectedKitchen?.id || (!isLoading && (!isOffersMenu || !hasProducts))) {
        return null
    }

    return (
        <Box className={classes.container}>
            <LocalizedStrict id="user-home-canteen-offers">
                <Typography variant="h6" marginBottom="1rem" fontWeight="bold">
                    Offers
                </Typography>
            </LocalizedStrict>
            <Box className={classes.newsGrid}>
                {isLoading ? (
                    <>
                        <Skeleton variant="rectangular" className={classes.skeletonCard} />
                        <Skeleton variant="rectangular" className={classes.skeletonCard} />
                        <Skeleton variant="rectangular" className={classes.skeletonCard} />
                    </>
                ) : (
                    <ErrorBoundary
                        fallback={
                            <UserDashboardErrorFallbackCard
                                classNames={classes.errorCard}
                                contentClassNames={classes.errorContent}
                            />
                        }
                    >
                        <>
                            {isOffersMenu ? (
                                offersMenu?.flatMap((menu) =>
                                    menu.products.map((product: Products) => (
                                        <ProductOffer key={product.id} product={product} userId={userId} />
                                    )),
                                )
                            ) : (
                                <ProductOfferEmpty />
                            )}
                        </>
                    </ErrorBoundary>
                )}
            </Box>
        </Box>
    )
}

const OffersFallback = () => {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()

    return (
        <Box marginBottom="3rem">
            <Box className={classes.container}>
                <UserDashboardErrorFallbackCard
                    classNames={classes.errorCard}
                    contentClassNames={classes.errorContent}
                    errorMessage={l10n.getString("user-dashboard-error-offers-unavailable")}
                >
                    <LocalizedStrict id="user-home-canteen-offers">
                        <Typography variant="h6" marginBottom="1rem" fontWeight="bold">
                            Offers
                        </Typography>
                    </LocalizedStrict>
                </UserDashboardErrorFallbackCard>
            </Box>
        </Box>
    )
}

export function OffersSection() {
    return (
        <QueryErrorBoundary Fallback={OffersFallback}>
            <SafeOffersSection />
        </QueryErrorBoundary>
    )
}
